// Helpers
import { getEnv, getCSGLink, getBaseUrl } from './utils/helpers';

// Constants
const APP_ENV: string = getEnv();
const BASE_URL: string = getBaseUrl(APP_ENV);
// Selector names
const SEL_MASTHEAD: string = '.mu-masthead';
const SEL_STICKY_LINKS_INITIAL: string = '.sticky-links-initial';
const SEL_STICKY_LINKS: string = '.sticky-links';
const SEL_SIGNIN_BTNS: string = '.signin-button';
const SEL_REGISTER_BTNS: string = '.register-button';
const SEL_CSG_BTNS: string = '.csg-button';
const SEL_MARVEL_BTNS: string = '.marvel-button';
const SEL_LOCAL_BTNS: string = '.local-button';
const SEL_LOADING_MODAL: string = '#loading-modal';
const SEL_FAQ_TABS: string = '.tab-title';
const SEL_FAQ_TABS_LINKS: string = '.faq-link';
const SEL_FAQ_CSG_LINKS: string = '.faq-csg-link';
const SEL_CURRENT_YEAR: string = '.current-year';

// Variables
let mastheadObserver: IntersectionObserver;
// DOM elements
let loadingModalEl: HTMLElement;
let mastheadEl: HTMLElement;
let stickyLinksInitialEl: HTMLElement;
let stickyLinksEl: HTMLElement;
let currentYearEl: HTMLElement;
let registerLinksEls: NodeListOf<HTMLAnchorElement>;
let signinLinksEls: NodeListOf<HTMLAnchorElement>;
let csgLinksEls: NodeListOf<HTMLAnchorElement>;
let marvelLinksEls: NodeListOf<HTMLAnchorElement>;
let localLinksEls: NodeListOf<HTMLAnchorElement>;
let faqTabs: NodeListOf<HTMLElement>;
let faqCSQLinks: NodeListOf<HTMLAnchorElement>;
let faqTabsLinks: NodeListOf<HTMLAnchorElement>;

// Get url params from the url
const _getURLParams = (): string | undefined => {
  const { search }: { search: string } = new URL(window.location.href);
  const extraParams: string = search.split('?')[1];

  return extraParams;
};

// Build a new CSG link based on the environment
const _buildCSGLink = (csgElement: HTMLAnchorElement): string => {
  const extraParams: string | undefined = _getURLParams();
  const csgLink: string = getCSGLink();
  const csgHref: string = csgElement.href;
  const csgRoute: string = csgHref.split('.com')[1];
  const csgParams: string = csgRoute.split('?')[1];
  const newCSGLink: string =
    csgLink +
    csgRoute +
    (extraParams ? (csgParams ? `&${extraParams}` : `?${extraParams}`) : '');

  return newCSGLink;
};

// Set urls to buttons and links based on the environment
const _setNewButtonLinks = (): void => {
  // Fix the links out to CSG for the environment
  Array.from(csgLinksEls).forEach(($link: HTMLAnchorElement): void => {
    const newHref: string = _buildCSGLink($link);

    if (newHref.length > 0) {
      $link.removeAttribute('href');
      $link.setAttribute('data-click-url', newHref);
    }
  });

  // Fix the csg account link for the environment
  Array.from(faqCSQLinks).forEach(($link: HTMLAnchorElement): void => {
    const hrefPath: string | undefined = $link.dataset.clickUrl;

    if (hrefPath && hrefPath.length > 0) {
      $link.href = hrefPath;
      $link.innerText = hrefPath;
    }
  });

  // Fix the links out to unlimited home for the environment
  Array.from(marvelLinksEls).forEach(($link: HTMLAnchorElement) => {
    const hrefPath: string = $link.href.split('.com')[1];
    const newHref: string = hrefPath && BASE_URL + hrefPath;

    if (newHref.length > 0) {
      $link.removeAttribute('href');
      $link.setAttribute('data-click-url', newHref);
    }
  });

  // Fix any other local marvel links for the environment
  Array.from(localLinksEls).forEach(($link: HTMLAnchorElement) => {
    const hrefPath: string = $link.href.split('.com')[1];
    const newHref: string = hrefPath && BASE_URL + hrefPath;

    if (newHref.length > 0) {
      $link.href = newHref;
    }
  });
};

// Set year in footer
const _setDOMFooterYear = (): void => {
  if (currentYearEl) {
    currentYearEl.innerText = String(new Date().getFullYear());
  }
};

// Listen to masthead for intersection observer
const _listenToDOMMasthead = (): void => {
  const _onElIntersection = (e: any): void => {
    const isIntersecting: boolean = e[0].isIntersecting;

    if (isIntersecting) {
      if (stickyLinksEl) {
        stickyLinksInitialEl.classList.add('visible');
        stickyLinksEl.classList.remove('visible');
      }
    } else {
      if (stickyLinksEl) {
        stickyLinksInitialEl.classList.remove('visible');
        stickyLinksEl.classList.add('visible');
      }
    }
  };

  mastheadObserver = new IntersectionObserver(_onElIntersection, {
    root: null,
    rootMargin: '-' + stickyLinksInitialEl.offsetHeight + 'px',
    threshold: 0,
  });

  mastheadObserver.observe(mastheadEl);
};

// Listen to FAQ tabs and links
const _listenToFAQTabs = (): void => {
  Array.from(faqTabs).forEach(($tab: HTMLElement) => {
    $tab.addEventListener('keydown', _handleFAQTabEvent);
    $tab.addEventListener('click', _handleFAQTabEvent);
  });

  Array.from(faqTabsLinks).forEach(($link: HTMLAnchorElement) => {
    $link.addEventListener('focus', _handleFAQLinkEvent);
  });
};

// Listen to MU Session creation
const _listenToMUSession = (referer: string): void => {
  // Remove any listeners
  window.removeEventListener(
    'oneIdModalClosed',
    _renderLoadingModal
  );
  window.removeEventListener(
    'muSessionEstablished',
    _handleRedirectForReferer.bind(null, referer)
  );
  // Add them back
  window.addEventListener(
    'oneIdModalClosed',
    _renderLoadingModal
  );
  window.addEventListener(
    'muSessionEstablished',
    _handleRedirectForReferer.bind(null, referer)
  );

  window['oneIdService'].authenticateForCheckout(true);
};

// Listen to OneID logged in events
const _listenToOneIdEvents = (referer: string, isMUCheckout?: boolean) => {
  if (isMUCheckout) {
    // Reset and relisten
    window.removeEventListener(
      'oneIdUserLoggedIn',
      _listenToMUSession.bind(null, referer)
    );
    window.addEventListener(
      'oneIdUserLoggedIn',
      _listenToMUSession.bind(null, referer)
    );
  } else {
    // Reset and relisten
    window.removeEventListener(
      'oneIdUserLoggedIn',
      _handleRedirectForReferer.bind(null, referer)
    );
    window.addEventListener(
      'oneIdUserLoggedIn',
      _handleRedirectForReferer.bind(null, referer)
    );
  }
};

// Listen to register and signin events
const _listenToOneIdButtons = (): void => {
  registerLinksEls.forEach(($el: HTMLAnchorElement) => {
    $el.addEventListener('click', _handleRegisterEvent);
    $el.addEventListener('keypress', _handleA11yRegisterEvent);
  });
  signinLinksEls.forEach(($el: HTMLAnchorElement) => {
    $el.addEventListener('click', _handleSigninEvent);
    $el.addEventListener('keypress', _handleA11ySigninEvent);
  });
};

// Listen to One ID initialization
const _listenToOneIdInit = () => {
  const _honeyBadger: any = window['HoneyBadger'];

  window.addEventListener('honeyBadgerSnikt', _listenToOneIdButtons);

  // DEV
  if (APP_ENV === 'development' && _honeyBadger) {
    const honeyBadger: any = new _honeyBadger();
    honeyBadger.loadDisneyTags('modal');
  }
};

// Handle toggling the FAQ dropdowns
const _handleFAQTabEvent = (e: any) => {
  const key: string = e.key;
  const $target: HTMLElement = e.target;
  const $content: Element | null = $target.nextElementSibling;
  const didA11yEnterOrClick: boolean = key === 'Enter' || !key;

  if (didA11yEnterOrClick && $content) {
    $target.classList.toggle('show');
    $content.classList.toggle('show');
  }
};

// Expand the tab content when a link within is focused
const _handleFAQLinkEvent = (e: any) => {
  const target: HTMLAnchorElement = e.currentTarget;
  const nearestTabContent: HTMLElement | null = target.closest('.tab-content');
  const nearestTabTitle: Element | null | undefined =
    nearestTabContent?.previousElementSibling;

  if (nearestTabTitle && nearestTabContent) {
    nearestTabTitle.classList.add('show');
    nearestTabContent.classList.add('show');
  }
};

// Handle a register button event
const _handleRegisterEvent = (e: Event): void => {
  e.preventDefault();
  const target: HTMLAnchorElement = e.currentTarget as HTMLAnchorElement;
  const referer: string | undefined = target.dataset.clickUrl;
  const _oneIdService: any = window['oneIdService'];

  if (referer && _oneIdService && _oneIdService._inited) {
    if (_oneIdService.loggedIn) {
      _listenToMUSession(referer);
      _renderLoadingModal();
    } else {
      _listenToOneIdEvents(referer, true);
      _oneIdService.startRegistration();
    }
  }
};

// Handle an a11y keyboard event on register
const _handleA11yRegisterEvent = (evt: KeyboardEvent): void => {
  evt.preventDefault();
  const key: string = evt.key;

  if (key === 'Enter') {
    _handleRegisterEvent(evt);
  }
};

// handle a signin button event
const _handleSigninEvent = (e: any): void => {
  e.preventDefault();
  const key: string = e.key;
  const target: HTMLAnchorElement = e.currentTarget;
  const referer: string | undefined = target.dataset.clickUrl;
  const didA11yEnterOrClick: boolean = key === 'Enter' || !key;

  if (
    referer &&
    didA11yEnterOrClick &&
    window['oneIdService'] &&
    window['oneIdService']._inited
  ) {
    if (window['oneIdService'].loggedIn) {
      _handleRedirectForReferer(referer);
    } else {
      _listenToOneIdEvents(referer);
      window['oneIdService'].startLogin();
    }
  }
};

// Handle an a11y keybaord event on signin
const _handleA11ySigninEvent = (evt: KeyboardEvent): void => {
  evt.preventDefault();
  const key: string = evt.key;

  if (key === 'Enter') {
    _handleSigninEvent(evt);
  }
};

// Redirect a user to a referer/link
const _handleRedirectForReferer = (referer: string): void => {
  window.location.href = referer;
};

// Render the loading modal
const _renderLoadingModal = (): void => {
  loadingModalEl.classList.add('show');
};

// Bind all DOM elements
const _bindDOMElements = (): void => {
  loadingModalEl = document.querySelector(SEL_LOADING_MODAL) as HTMLElement;
  mastheadEl = document.querySelector(SEL_MASTHEAD) as HTMLElement;
  stickyLinksInitialEl = document.querySelector(
    SEL_STICKY_LINKS_INITIAL
  ) as HTMLElement;
  stickyLinksEl = document.querySelector(SEL_STICKY_LINKS) as HTMLElement;
  currentYearEl = document.querySelector(SEL_CURRENT_YEAR) as HTMLElement;
  registerLinksEls = document.querySelectorAll(SEL_REGISTER_BTNS);
  signinLinksEls = document.querySelectorAll(SEL_SIGNIN_BTNS);
  csgLinksEls = document.querySelectorAll(SEL_CSG_BTNS);
  marvelLinksEls = document.querySelectorAll(SEL_MARVEL_BTNS);
  localLinksEls = document.querySelectorAll(SEL_LOCAL_BTNS);
  faqTabs = document.querySelectorAll(SEL_FAQ_TABS);
  faqCSQLinks = document.querySelectorAll(SEL_FAQ_CSG_LINKS);
  faqTabsLinks = document.querySelectorAll(SEL_FAQ_TABS_LINKS);
};

const _initialize = (): void => {
  _bindDOMElements();
  _listenToOneIdInit();
  _listenToFAQTabs();

  // Since this application lives inside a React application we need to wait to do any DOM manpilation
  // otherwise we collide with React working on the DOM. One second seems to be enough time.
  setTimeout((): void => {
    _setNewButtonLinks();
    _listenToDOMMasthead();
    _setDOMFooterYear();
  }, 1000);
};

window.addEventListener('DOMContentLoaded', _initialize);
